import React from 'react';
import { isbot } from 'isbot';
import type { GetServerSideProps, GetServerSidePropsContext } from 'next';

import { serverSideTranslations } from 'next-i18next/serverSideTranslations';

import Header from 'src/components/other/Header';
import { routePathAd, routePathLogin } from 'src/constants/router';
import { SearchAdsPageContent } from 'src/components/ad/SearchAdsPage';
import { useSignInVisitor } from 'src/hooks/useSignInVisitor';
import { QuickSignupModal } from 'src/components/auth/QuickSignupModal';
import { getAuthCookie } from 'src/utils/authCookie';

const AdListPage: PageComponent = ({ isUserAgentBot }: AdPageProps) => {
    const { isModalOpen, infos, closeModal } = useSignInVisitor();
    return (
        <>
            <Header />
            <SearchAdsPageContent isUserAgentBot={isUserAgentBot === true} />
            <QuickSignupModal open={isModalOpen} onClose={closeModal} userPrefilledInfos={infos} />
        </>
    );
};

type AdPageProps = PageProps & {
    isUserAgentBot?: boolean;
};

export const getServerSideProps: GetServerSideProps<AdPageProps> = async (context: GetServerSidePropsContext) => {
    const auth = getAuthCookie(context);
    // Redirect to login page if the user is not authenticated and the searchId query parameter is present
    // After login, the user will be redirected back to the current page
    if (!auth && context.query.searchId) {
        return {
            redirect: {
                permanent: false,
                destination: `${routePathLogin}?returnUrl=${encodeURIComponent(context.resolvedUrl)}`,
            },
            props: {},
        };
    }

    const isUserAgentBot = isbot(context.req.headers['user-agent']);
    // Redirect to list view if the user agent is a bot to reduce the amount of data queried and improve performance
    if (isUserAgentBot && context.query.viewMode !== 'list') {
        const params = new URLSearchParams({
            ...context.query,
            viewMode: 'list',
        });
        return {
            redirect: {
                permanent: false,
                destination: `${routePathAd}?${params.toString()}`,
            },
            props: {},
        };
    }

    return {
        props: {
            isUserAgentBot,
            ...(await serverSideTranslations(context.locale as string, [
                'common',
                'ad',
                'favorites',
                'profile',
                'notifications',
            ])),
            SEOProps: {
                pageTitleTranslation: {
                    key: 'common:seo.ad.list.title',
                },
                metaDescriptionTranslation: {
                    key: 'common:seo.ad.list.description',
                },
            },
        },
    };
};

export default AdListPage;
